import React, { useState, useEffect, useCallback, useMemo, useId } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BookOpen, ChevronDown, Layers, Library, Pencil, Plus, TriangleAlert, Upload,
} from 'lucide-react';
import {
  getCourseVocabulary,
  getLessonVocabulary,
  replaceLessonVocabulary,
  addVocabularyItem,
  reorderLessonVocabulary,
  updateVocabularyItem,
  deleteVocabularyItem,
  setVocabularyItemImage,
} from '../../api/vocabulary';
import MediaSlot from '../../components/MediaSlot/MediaSlot';
import { unwrapList } from '../../utils/api';
import errorText from '../../utils/errorText';
import Dropdown from '../../components/Dropdown/Dropdown';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import MetricCard from '../../components/MetricCard/MetricCard';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import toast from 'react-hot-toast';
import { useI18n } from '../../i18n/I18nContext';
import { pluralKey } from '../../utils/plural';
import { parseWordList, buildImportItems, termKey } from './wordListImport';
import './CourseVocabulary.css';

const PARTS_OF_SPEECH = [
  'NOUN', 'VERB', 'ADJECTIVE', 'ADVERB', 'PRONOUN',
  'NUMERAL', 'PREPOSITION', 'CONJUNCTION', 'PARTICLE', 'PHRASE',
];
const GENDERS = ['MASCULINE', 'FEMININE', 'NEUTER', 'PLURAL'];
const ASPECTS = ['IMPERFECTIVE', 'PERFECTIVE', 'BOTH'];

/** Parallel per-lesson reads on load; the API's short-window limit is 20/s. */
const LESSON_FETCH_CONCURRENCY = 5;

/** POST /vocabulary/lessons/:id takes at most 500 words (ReplaceVocabularyDto). */
const MAX_LESSON_WORDS = 500;

/** Where the curriculum's lessons section opens its "add lesson" form. */
const ADD_LESSON_SEARCH = '?tab=curriculum&c=lessons&add=1';

const EMPTY_WORD = {
  term: '',
  termStressed: '',
  transliteration: '',
  partOfSpeech: 'NOUN',
  gender: '',
  aspect: '',
  translationUz: '',
  translationEn: '',
  exampleRu: '',
  exampleUz: '',
  exampleEn: '',
  audioUrl: '',
};

/** The API keeps optional columns as null — the form wants empty strings. */
function fromItem(item) {
  return {
    term: item.term || '',
    termStressed: item.termStressed || '',
    transliteration: item.transliteration || '',
    partOfSpeech: item.partOfSpeech || 'NOUN',
    gender: item.gender || '',
    aspect: item.aspect || '',
    translationUz: item.translationUz || '',
    translationEn: item.translationEn || '',
    exampleRu: item.exampleRu || '',
    exampleUz: item.exampleUz || '',
    exampleEn: item.exampleEn || '',
    audioUrl: item.audioUrl || '',
  };
}

/** Form state → VocabItemInput: trimmed, blanks as null, gender/aspect only where they apply. */
function toPayload(form) {
  const orNull = (v) => (v && v.trim() ? v.trim() : null);
  return {
    term: form.term.trim(),
    termStressed: orNull(form.termStressed),
    transliteration: orNull(form.transliteration),
    partOfSpeech: form.partOfSpeech,
    gender: form.partOfSpeech === 'NOUN' ? (form.gender || null) : null,
    aspect: form.partOfSpeech === 'VERB' ? (form.aspect || null) : null,
    translationUz: form.translationUz.trim(),
    translationEn: form.translationEn.trim(),
    exampleRu: orNull(form.exampleRu),
    exampleUz: orNull(form.exampleUz),
    exampleEn: orNull(form.exampleEn),
    audioUrl: orNull(form.audioUrl),
  };
}

/**
 * `picture` (an existing word only): { url, onChange(url) } — the picture has its own route and is
 * saved the moment it is uploaded, while the form's other fields wait for Save.
 */
function WordForm({ initial, saving, submitLabel, onSubmit, onCancel, picture }) {
  const { t } = useI18n();
  const uid = useId();
  const fid = (key) => `${uid}-${key}`;
  const [form, setForm] = useState(() => ({ ...EMPTY_WORD, ...initial }));

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  // English is optional: the API takes an empty translationEn, and students see Uzbek.
  const valid = form.term.trim() && form.translationUz.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    onSubmit(toPayload(form));
  };

  return (
    <form className="cv-word-form" onSubmit={handleSubmit}>
      <div className="cv-form-grid">
        <div className="cv-form-field">
          <label className="cv-label" htmlFor={fid('term')}>{t('vocabulary.termLabel')} <em className="cv-required">*</em></label>
          <input id={fid('term')} className="cv-input" type="text" maxLength={120} placeholder={t('vocabulary.termPlaceholder')} value={form.term} onChange={set('term')} disabled={saving} />
        </div>
        <div className="cv-form-field">
          <label className="cv-label" htmlFor={fid('stressed')}>{t('vocabulary.termStressedLabel')}</label>
          <input id={fid('stressed')} className="cv-input" type="text" maxLength={160} placeholder={t('vocabulary.termStressedPlaceholder')} value={form.termStressed} onChange={set('termStressed')} disabled={saving} />
        </div>
        <div className="cv-form-field">
          <label className="cv-label" htmlFor={fid('translit')}>{t('vocabulary.transliterationLabel')}</label>
          <input id={fid('translit')} className="cv-input" type="text" maxLength={160} placeholder={t('vocabulary.transliterationPlaceholder')} value={form.transliteration} onChange={set('transliteration')} disabled={saving} />
        </div>
        <div className="cv-form-field">
          <label className="cv-label" htmlFor={fid('pos')}>{t('vocabulary.partOfSpeechLabel')}</label>
          <Dropdown
            id={fid('pos')}
            value={form.partOfSpeech}
            onChange={(val) => setForm((f) => ({ ...f, partOfSpeech: val }))}
            options={PARTS_OF_SPEECH.map((p) => ({ value: p, label: t(`vocabulary.partOfSpeech.${p}`) }))}
            ariaLabel={t('vocabulary.partOfSpeechLabel')}
          />
        </div>
        {form.partOfSpeech === 'NOUN' && (
          <div className="cv-form-field">
            <label className="cv-label" htmlFor={fid('gender')}>{t('vocabulary.genderLabel')}</label>
            <Dropdown
              id={fid('gender')}
              value={form.gender}
              onChange={(val) => setForm((f) => ({ ...f, gender: val }))}
              options={[
                { value: '', label: t('vocabulary.notSet') },
                ...GENDERS.map((g) => ({ value: g, label: t(`vocabulary.gender.${g}`) })),
              ]}
              ariaLabel={t('vocabulary.genderLabel')}
            />
          </div>
        )}
        {form.partOfSpeech === 'VERB' && (
          <div className="cv-form-field">
            <label className="cv-label" htmlFor={fid('aspect')}>{t('vocabulary.aspectLabel')}</label>
            <Dropdown
              id={fid('aspect')}
              value={form.aspect}
              onChange={(val) => setForm((f) => ({ ...f, aspect: val }))}
              options={[
                { value: '', label: t('vocabulary.notSet') },
                ...ASPECTS.map((a) => ({ value: a, label: t(`vocabulary.aspect.${a}`) })),
              ]}
              ariaLabel={t('vocabulary.aspectLabel')}
            />
          </div>
        )}
        <div className="cv-form-field">
          <label className="cv-label" htmlFor={fid('uz')}>{t('vocabulary.translationUzLabel')} <em className="cv-required">*</em></label>
          <input id={fid('uz')} className="cv-input" type="text" maxLength={200} placeholder={t('vocabulary.translationUzPlaceholder')} value={form.translationUz} onChange={set('translationUz')} disabled={saving} />
        </div>
        <div className="cv-form-field">
          <label className="cv-label" htmlFor={fid('en')}>{t('gameAuthoring.words.englishOptional')}</label>
          <input id={fid('en')} className="cv-input" type="text" maxLength={200} placeholder={t('vocabulary.translationEnPlaceholder')} value={form.translationEn} onChange={set('translationEn')} disabled={saving} />
        </div>
        <div className="cv-form-field cv-form-field-wide">
          <label className="cv-label" htmlFor={fid('exru')}>{t('vocabulary.exampleRuLabel')}</label>
          <input id={fid('exru')} className="cv-input" type="text" maxLength={400} placeholder={t('vocabulary.exampleRuPlaceholder')} value={form.exampleRu} onChange={set('exampleRu')} disabled={saving} />
        </div>
        <div className="cv-form-field">
          <label className="cv-label" htmlFor={fid('exuz')}>{t('vocabulary.exampleUzLabel')}</label>
          <input id={fid('exuz')} className="cv-input" type="text" maxLength={400} placeholder={t('vocabulary.exampleUzPlaceholder')} value={form.exampleUz} onChange={set('exampleUz')} disabled={saving} />
        </div>
        <div className="cv-form-field">
          <label className="cv-label" htmlFor={fid('exen')}>{t('vocabulary.exampleEnLabel')}</label>
          <input id={fid('exen')} className="cv-input" type="text" maxLength={400} placeholder={t('vocabulary.exampleEnPlaceholder')} value={form.exampleEn} onChange={set('exampleEn')} disabled={saving} />
        </div>
        <div className="cv-form-field cv-form-field-wide">
          <span className="cv-label">{t('mediaSlot.wordAudio')}</span>
          <MediaSlot
            kind="audio"
            value={form.audioUrl}
            onChange={(url) => setForm((f) => ({ ...f, audioUrl: url || '' }))}
            disabled={saving}
          />
        </div>
        {picture && (
          <div className="cv-form-field cv-form-field-wide">
            <span className="cv-label">{t('mediaSlot.wordPicture')}</span>
            <MediaSlot kind="image" value={picture.url || ''} onChange={picture.onChange} removable={false} disabled={saving} />
            <span className="cv-hint">{t('mediaSlot.wordPictureHint')}</span>
          </div>
        )}
      </div>
      <div className="cv-form-actions">
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={onCancel} disabled={saving}>{t('vocabulary.cancel')}</button>
        <button type="submit" className={`ds-btn ds-btn-primary ds-btn-sm ${saving ? 'is-loading' : ''}`} disabled={saving || !valid}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function BulkImport({ lessonId, existingWords, confirm, onImported, onCancel }) {
  const { t, lang } = useI18n();
  const [text, setText] = useState('');
  const [mode, setMode] = useState('append');
  const [step, setStep] = useState('input');
  const [importing, setImporting] = useState(false);

  const { rows, errors } = useMemo(() => parseWordList(text), [text]);
  const plan = useMemo(() => buildImportItems(existingWords, rows, mode), [existingWords, rows, mode]);
  // A word the lesson already has keeps the English it has, so a two-column paste does not blank it.
  const stored = useMemo(() => new Map(existingWords.map((w) => [termKey(w.term), w])), [existingWords]);
  const tooMany = plan.items.length > MAX_LESSON_WORDS;

  const handleImport = async () => {
    if (rows.length === 0 || tooMany) return;
    if (mode === 'replace') {
      const ok = await confirm({
        title: t('gameAuthoring.words.confirmReplaceTitle'),
        description: plan.removed > 0
          ? t(pluralKey('gameAuthoring.words.confirmReplaceDesc', plan.removed, lang), { count: plan.removed })
          : t('vocabulary.bulk.replaceWarning'),
        confirmLabel: t('gameAuthoring.words.confirmReplace'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    setImporting(true);
    try {
      // "Add" sends the stored words along with the new ones, so it is built from a fresh read:
      // a word someone else added since this page loaded must not be dropped by the import.
      let result = plan;
      if (mode === 'append') {
        const latest = unwrapList(await getLessonVocabulary(lessonId));
        result = buildImportItems(latest, rows, 'append');
        if (result.items.length > MAX_LESSON_WORDS) {
          toast.error(t('gameAuthoring.words.tooMany', { count: result.items.length }));
          return;
        }
      }
      await replaceLessonVocabulary(lessonId, { items: result.items });
      toast.success(t('gameAuthoring.words.importedSummary', { added: result.added, updated: result.updated }));
      try {
        await onImported();
      } catch {
        toast.error(t('gameAuthoring.words.reloadFailed'));
      }
    } catch (err) {
      toast.error(errorText(err, t('vocabulary.bulk.importFailed')));
    } finally {
      setImporting(false);
    }
  };

  const importLabel = mode === 'replace'
    ? t(pluralKey('vocabulary.bulk.importAction', rows.length, lang), { count: rows.length })
    : plan.added > 0
      ? t(pluralKey('gameAuthoring.words.appendAction', plan.added, lang), { count: plan.added })
      : t('gameAuthoring.words.saveAction');

  const lineErrors = errors.length > 0 && (
    <div className="ui-alert cv-bulk-errors" data-tone="warning" role="status">
      <TriangleAlert aria-hidden="true" />
      <div>
        <p className="cv-bulk-errors-title">
          {t(pluralKey('gameAuthoring.words.errorsTitle', errors.length, lang), { count: errors.length })}
        </p>
        <ul>
          {errors.slice(0, 20).map((err) => (
            <li key={err.line}>
              <strong>{t('gameAuthoring.words.lineLabel', { line: err.line })}</strong>
              {' '}
              <span className="cv-bulk-errors-text">{err.text}</span>
              {' — '}
              {t(`gameAuthoring.words.reason.${err.reason}`, {
                firstLine: err.firstLine,
                column: err.column ? t(`gameAuthoring.words.column.${err.column}`) : '',
              })}
            </li>
          ))}
          {errors.length > 20 && <li>…</li>}
        </ul>
      </div>
    </div>
  );

  return (
    <div className="cv-bulk">
      <div className="cv-bulk-head">
        <span className="cv-bulk-title">{t('vocabulary.bulk.title')}</span>
        <code className="cv-bulk-format">{t('gameAuthoring.words.formatHint')}</code>
      </div>

      <div className="cv-mode" role="radiogroup" aria-label={t('gameAuthoring.words.modeLabel')}>
        {[
          { value: 'append', label: t('gameAuthoring.words.modeAppend'), hint: t('gameAuthoring.words.modeAppendHint') },
          { value: 'replace', label: t('gameAuthoring.words.modeReplace'), hint: t('gameAuthoring.words.modeReplaceHint') },
        ].map((opt) => (
          <label key={opt.value} className={`cv-mode-option ${mode === opt.value ? 'is-active' : ''}`}>
            <input
              type="radio"
              name={`cv-mode-${lessonId}`}
              value={opt.value}
              checked={mode === opt.value}
              onChange={() => setMode(opt.value)}
              disabled={importing}
            />
            <span className="cv-mode-text">
              <span className="cv-mode-label">{opt.label}</span>
              <span className="cv-mode-hint">{opt.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {step === 'input' ? (
        <>
          <p className="cv-bulk-hint">{t('gameAuthoring.words.hint')}</p>
          <textarea
            className="cv-textarea"
            rows={8}
            spellCheck={false}
            placeholder={t('gameAuthoring.words.placeholder')}
            aria-label={t('vocabulary.bulk.title')}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {lineErrors}
          <div className="cv-form-actions">
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={onCancel}>{t('vocabulary.cancel')}</button>
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setText('')} disabled={!text}>{t('vocabulary.bulk.clear')}</button>
            <button type="button" className="ds-btn ds-btn-primary ds-btn-sm" onClick={() => setStep('preview')} disabled={rows.length === 0}>
              {t('vocabulary.bulk.parseAction')}
            </button>
          </div>
          {text.trim() && rows.length === 0 && errors.length === 0 && (
            <p className="cv-empty-hint">{t('vocabulary.bulk.noRowsParsed')}</p>
          )}
        </>
      ) : (
        <>
          <div className="cv-bulk-summary">
            <span className="cv-bulk-count">
              {t(pluralKey('vocabulary.bulk.previewCount', rows.length, lang), { count: rows.length })}
            </span>
            {plan.added > 0 && (
              <span className="ds-badge ds-badge-success">
                {t(pluralKey('gameAuthoring.words.addedCount', plan.added, lang), { count: plan.added })}
              </span>
            )}
            {plan.updated > 0 && (
              <span className="ds-badge ds-badge-info">
                {t(pluralKey('gameAuthoring.words.updatedCount', plan.updated, lang), { count: plan.updated })}
              </span>
            )}
            {plan.kept > 0 && (
              <span className="ds-badge ds-badge-neutral">
                {t(pluralKey('gameAuthoring.words.keptCount', plan.kept, lang), { count: plan.kept })}
              </span>
            )}
            {plan.removed > 0 && (
              <span className="ds-badge ds-badge-danger">
                {t(pluralKey('gameAuthoring.words.removedCount', plan.removed, lang), { count: plan.removed })}
              </span>
            )}
          </div>

          <div className="cv-bulk-table-wrap">
            <table className="cv-bulk-table" aria-label={t('vocabulary.previewTableAria')}>
              <thead>
                <tr>
                  <th className="cv-bulk-col-num">{t('vocabulary.bulk.colNum')}</th>
                  <th>{t('vocabulary.bulk.colRu')}</th>
                  <th>{t('vocabulary.bulk.colUz')}</th>
                  <th>{t('vocabulary.bulk.colEn')}</th>
                  <th>{t('gameAuthoring.words.colExample')}</th>
                  <th>{t('gameAuthoring.words.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const match = stored.get(termKey(row.term));
                  const english = row.translationEn || match?.translationEn || '';
                  return (
                    <tr key={row.line}>
                      <td className="cv-bulk-col-num">{row.line}</td>
                      <td className="cv-bulk-ru">{row.term}</td>
                      <td>{row.translationUz}</td>
                      <td className={english ? '' : 'cv-bulk-blank'}>{english || '—'}</td>
                      <td className={row.exampleRu ? 'cv-bulk-example' : 'cv-bulk-blank'}>{row.exampleRu || '—'}</td>
                      <td>
                        <span className={`ds-badge ${match ? 'ds-badge-info' : 'ds-badge-success'}`}>
                          {t(match ? 'gameAuthoring.words.statusUpdate' : 'gameAuthoring.words.statusNew')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {lineErrors}

          {mode === 'replace' ? (
            <div className="ui-alert" data-tone="warning">
              <TriangleAlert aria-hidden="true" />
              <p>{t('vocabulary.bulk.replaceWarning')}</p>
            </div>
          ) : plan.added === 0 && (
            <p className="cv-empty-hint">{t('gameAuthoring.words.nothingNew')}</p>
          )}
          {tooMany && (
            <div className="ui-alert" data-tone="danger" role="alert">
              <TriangleAlert aria-hidden="true" />
              <p>{t('gameAuthoring.words.tooMany', { count: plan.items.length })}</p>
            </div>
          )}

          <div className="cv-form-actions">
            <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setStep('input')} disabled={importing}>
              {t('vocabulary.bulk.back')}
            </button>
            <button
              type="button"
              className={`ds-btn ${mode === 'replace' ? 'ds-btn-danger' : 'ds-btn-primary'} ds-btn-sm ${importing ? 'is-loading' : ''}`}
              onClick={handleImport}
              disabled={importing || rows.length === 0 || tooMany}
            >
              {importLabel}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function CourseVocabulary({ courseId }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  // `?lesson=<id>` (the games tab's readiness checklist links here) opens that lesson's words
  const [searchParams, setSearchParams] = useSearchParams();
  const focusLessonId = searchParams.get('lesson');
  const [confirm, confirmDialog] = useConfirmDialog();
  const [lessons, setLessons] = useState([]);
  const [wordsByLesson, setWordsByLesson] = useState({});
  const [loading, setLoading] = useState(true);
  // null | 'forbidden' | 'failed'
  const [loadError, setLoadError] = useState(null);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [showAdd, setShowAdd] = useState(null);
  const [showBulk, setShowBulk] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [failedLessons, setFailedLessons] = useState({});
  const [retryingLesson, setRetryingLesson] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const overview = await getCourseVocabulary(courseId);
      const lessonList = Array.isArray(overview?.lessons) ? overview.lessons : [];
      setLessons(lessonList);

      const wordsMap = {};
      const failed = {};
      // The API throttles bursts (20 requests/second) and a course can hold
      // dozens of lessons, so the per-lesson reads run a few at a time — an
      // all-at-once fan-out gets 429'd and drops real word lists.
      const queue = [...lessonList];
      const workers = Array.from(
        { length: Math.min(LESSON_FETCH_CONCURRENCY, queue.length) },
        async () => {
          while (queue.length > 0) {
            const lesson = queue.shift();
            try {
              const res = await getLessonVocabulary(lesson.lessonId);
              wordsMap[lesson.lessonId] = unwrapList(res);
            } catch {
              // Leave the lesson unloaded rather than storing an empty list:
              // a failed read must not render 50 real words as "no words yet".
              failed[lesson.lessonId] = true;
            }
          }
        },
      );
      await Promise.all(workers);
      setWordsByLesson(wordsMap);
      setFailedLessons(failed);
    } catch (err) {
      setLoadError(err.code === 403 ? 'forbidden' : 'failed');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (loading || !focusLessonId) return;
    if (lessons.some((l) => l.lessonId === focusLessonId)) {
      setExpandedLesson(focusLessonId);
      // After the expanded body renders
      window.requestAnimationFrame(() => {
        document.getElementById(`cv-section-${focusLessonId}`)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('lesson');
    setSearchParams(next, { replace: true });
  }, [loading, focusLessonId, lessons, searchParams, setSearchParams]);

  // Bulk replace rewrites every row, so the lesson is re-read instead of patched.
  const refreshLesson = useCallback(async (lessonId) => {
    const res = await getLessonVocabulary(lessonId);
    setWordsByLesson((prev) => ({ ...prev, [lessonId]: unwrapList(res) }));
    setFailedLessons((prev) => {
      if (!prev[lessonId]) return prev;
      const next = { ...prev };
      delete next[lessonId];
      return next;
    });
  }, []);

  const handleRetryLesson = async (lessonId) => {
    setRetryingLesson(lessonId);
    try {
      await refreshLesson(lessonId);
    } catch (err) {
      toast.error(errorText(err, t('vocabulary.loadWordsFailed')));
    } finally {
      setRetryingLesson(null);
    }
  };

  const handleAdd = async (lessonId, payload) => {
    setSaving(true);
    try {
      // The API upserts on (lesson, term), so a re-added term keeps its row.
      const created = await addVocabularyItem(lessonId, payload);
      setWordsByLesson((prev) => {
        const list = prev[lessonId] || [];
        const known = list.some((w) => w.id === created.id);
        return {
          ...prev,
          [lessonId]: known ? list.map((w) => (w.id === created.id ? created : w)) : [...list, created],
        };
      });
      setShowAdd(null);
      toast.success(t('vocabulary.wordCreated'));
    } catch (err) {
      toast.error(errorText(err, t('vocabulary.createWordFailed')));
    } finally {
      setSaving(false);
    }
  };

  // A word's picture is saved as soon as it is uploaded (its own route), then the lesson reloads.
  const handlePicture = async (lessonId, itemId, url) => {
    try {
      await setVocabularyItemImage(itemId, url);
      await refreshLesson(lessonId);
    } catch (err) {
      toast.error(errorText(err, t('mediaSlot.pictureSaveFailed')));
    }
  };

  const handleUpdate = async (lessonId, itemId, payload) => {
    setSaving(true);
    try {
      const updated = await updateVocabularyItem(itemId, payload);
      setWordsByLesson((prev) => ({
        ...prev,
        [lessonId]: (prev[lessonId] || []).map((w) => (w.id === itemId ? { ...w, ...updated } : w)),
      }));
      setEditingId(null);
      toast.success(t('vocabulary.wordUpdated'));
    } catch (err) {
      toast.error(errorText(err, t('vocabulary.updateWordFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lessonId, itemId) => {
    const ok = await confirm({
      title: t('vocabulary.deleteTitle'),
      description: t('vocabulary.deleteDesc'),
      confirmLabel: t('vocabulary.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    setDeleting(itemId);
    try {
      await deleteVocabularyItem(itemId);
      setWordsByLesson((prev) => ({
        ...prev,
        [lessonId]: (prev[lessonId] || []).filter((w) => w.id !== itemId),
      }));
      if (editingId === itemId) setEditingId(null);
      toast.success(t('vocabulary.wordDeleted'));
    } catch (err) {
      toast.error(errorText(err, t('vocabulary.deleteWordFailed')));
    } finally {
      setDeleting(null);
    }
  };

  // Optimistic swap — the server only stores the resulting order.
  const handleMove = async (lessonId, index, direction) => {
    const current = wordsByLesson[lessonId] || [];
    const target = index + direction;
    if (target < 0 || target >= current.length) return;

    const next = [...current];
    [next[index], next[target]] = [next[target], next[index]];
    setWordsByLesson((prev) => ({ ...prev, [lessonId]: next }));
    setReordering(true);
    try {
      await reorderLessonVocabulary(lessonId, next.map((w) => w.id));
    } catch (err) {
      setWordsByLesson((prev) => ({ ...prev, [lessonId]: current }));
      toast.error(errorText(err, t('vocabulary.reorderFailed')));
    } finally {
      setReordering(false);
    }
  };

  // Fall back to the overview's count only while a lesson's own list is missing.
  const countFor = (lesson) => (
    wordsByLesson[lesson.lessonId] ? wordsByLesson[lesson.lessonId].length : (lesson.itemCount || 0)
  );
  const totalWords = lessons.reduce((sum, lesson) => sum + countFor(lesson), 0);
  const lessonsWithWords = lessons.filter((lesson) => countFor(lesson) > 0);

  if (loading) {
    return (
      <div className="cv-page">
        <LoadingState skeleton="page" message={t('vocabulary.loadingWords')} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="cv-page">
        {loadError === 'forbidden' ? (
          <PageEmptyState variant="forbidden" size="sm" title={t('vocabulary.accessDenied')} />
        ) : (
          <PageEmptyState variant="error" size="sm" onRetry={fetchAll} />
        )}
      </div>
    );
  }

  return (
    <div className="cv-page">
      <p className="cv-lead">{t('vocabulary.pageSubtitle')}</p>

      <div className="kpi-row">
        <MetricCard countUp={false} title={t('vocabulary.kpiTotalWords')} value={totalWords} icon={<BookOpen />} tone="info" />
        <MetricCard countUp={false} title={t('vocabulary.kpiLessonsWithWords')} value={lessonsWithWords.length} icon={<Library />} tone="success" />
        <MetricCard countUp={false} title={t('vocabulary.kpiLessonsTotal')} value={lessons.length} icon={<Layers />} tone="purple" />
        <MetricCard countUp={false} title={t('vocabulary.kpiLessonsEmpty')} value={lessons.length - lessonsWithWords.length} icon={<Pencil />} tone="coral" />
      </div>

      {lessons.length === 0 && (
        <PageEmptyState
          variant="docs"
          size="sm"
          title={t('vocabulary.noLessonsTitle')}
          description={t('vocabulary.noLessonsDesc')}
          actionLabel={t('gameAuthoring.addLesson')}
          onAction={() => navigate({ search: ADD_LESSON_SEARCH })}
        />
      )}

      <div className="cv-lessons-list">
        {lessons.map((lesson, idx) => {
          const words = wordsByLesson[lesson.lessonId] || [];
          const isExpanded = expandedLesson === lesson.lessonId;
          const bodyId = `cv-lesson-${lesson.lessonId}`;

          return (
            <section key={lesson.lessonId} id={`cv-section-${lesson.lessonId}`} className={`section-card cv-lesson ${isExpanded ? 'cv-lesson-expanded' : ''}`}>
              <button
                type="button"
                className="cv-lesson-hd"
                onClick={() => setExpandedLesson(isExpanded ? null : lesson.lessonId)}
                aria-expanded={isExpanded}
                aria-controls={bodyId}
              >
                <span className="cv-lesson-num">{idx + 1}</span>
                <span className="cv-lesson-meta">
                  <span className="cv-lesson-name">{lesson.title}</span>
                  {lesson.module && <span className="cv-lesson-module">{lesson.module.title}</span>}
                </span>
                <span className={`ds-badge ${countFor(lesson) > 0 ? 'ds-badge-info' : 'ds-badge-neutral'} cv-words-count`}>
                  {t(pluralKey('vocabulary.wordCountLabel', countFor(lesson), lang), { count: countFor(lesson) })}
                </span>
                <ChevronDown className={`cv-chevron ${isExpanded ? 'is-open' : ''}`} size={18} aria-hidden="true" />
              </button>

              {isExpanded && (
                <div className="cv-lesson-body" id={bodyId}>
                  {failedLessons[lesson.lessonId] ? (
                    /* The lesson's own read failed (throttled or offline). Show a
                       retry instead of an empty list — bulk import replaces the
                       whole list, so it must not be offered over unknown data. */
                    <div className="ui-alert cv-lesson-error" role="alert">
                      <TriangleAlert aria-hidden="true" />
                      <p>{t('vocabulary.lessonLoadFailed')}</p>
                      <button
                        type="button"
                        className={`ds-btn ds-btn-secondary ds-btn-sm ${retryingLesson === lesson.lessonId ? 'is-loading' : ''}`}
                        onClick={() => handleRetryLesson(lesson.lessonId)}
                        disabled={retryingLesson === lesson.lessonId}
                      >
                        {t('ui.retry')}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="cv-lesson-toolbar">
                        <span className="cv-toolbar-label">
                          {t(pluralKey('vocabulary.wordsInLesson', words.length, lang), { count: words.length })}
                        </span>
                        <div className="cv-toolbar-actions">
                          {showBulk !== lesson.lessonId && (
                            <button
                              type="button"
                              className="ds-btn ds-btn-secondary ds-btn-sm"
                              onClick={() => { setShowBulk(lesson.lessonId); setShowAdd(null); setEditingId(null); }}
                            >
                              <Upload aria-hidden="true" />
                              {t('vocabulary.bulkImport')}
                            </button>
                          )}
                          {showAdd !== lesson.lessonId && (
                            <button
                              type="button"
                              className="ds-btn ds-btn-secondary ds-btn-sm"
                              onClick={() => { setShowAdd(lesson.lessonId); setShowBulk(null); setEditingId(null); }}
                            >
                              <Plus aria-hidden="true" />
                              {t('vocabulary.addWord')}
                            </button>
                          )}
                        </div>
                      </div>

                      {showBulk === lesson.lessonId && (
                        <BulkImport
                          lessonId={lesson.lessonId}
                          existingWords={words}
                          confirm={confirm}
                          onCancel={() => setShowBulk(null)}
                          onImported={async () => {
                            await refreshLesson(lesson.lessonId);
                            setShowBulk(null);
                          }}
                        />
                      )}

                      {showAdd === lesson.lessonId && (
                        <WordForm
                          initial={EMPTY_WORD}
                          saving={saving}
                          submitLabel={t('vocabulary.add')}
                          onCancel={() => setShowAdd(null)}
                          onSubmit={(payload) => handleAdd(lesson.lessonId, payload)}
                        />
                      )}

                      {words.length === 0 && showAdd !== lesson.lessonId && showBulk !== lesson.lessonId && (
                        <InlineEmpty kind="vocabulary" title={t('vocabulary.noWordsForLesson')} />
                      )}

                      {words.length > 0 && (
                        <div className="cv-word-list">
                          {words.map((word, wIdx) => (
                            editingId === word.id ? (
                              <div key={word.id} className="cv-word cv-word-editing">
                                <WordForm
                                  initial={fromItem(word)}
                                  saving={saving}
                                  submitLabel={t('vocabulary.save')}
                                  onCancel={() => setEditingId(null)}
                                  onSubmit={(payload) => handleUpdate(lesson.lessonId, word.id, payload)}
                                  picture={{
                                    url: word.imageUrl,
                                    onChange: (url) => url && handlePicture(lesson.lessonId, word.id, url),
                                  }}
                                />
                              </div>
                            ) : (
                              <div key={word.id} className="cv-word">
                                <span className="cv-word-index">{wIdx + 1}</span>
                                {word.imageUrl && <img className="cv-word-thumb" src={word.imageUrl} alt="" loading="lazy" />}

                                <div className="cv-word-body">
                                  <div className="cv-word-top">
                                    <span className="cv-word-term">{word.termStressed || word.term}</span>
                                    {word.transliteration && <span className="cv-word-translit">[{word.transliteration}]</span>}
                                    <span className="ds-badge ds-badge-purple">{t(`vocabulary.partOfSpeech.${word.partOfSpeech}`)}</span>
                                    {word.gender && <span className="ds-badge ds-badge-neutral">{t(`vocabulary.gender.${word.gender}`)}</span>}
                                    {word.aspect && <span className="ds-badge ds-badge-neutral">{t(`vocabulary.aspect.${word.aspect}`)}</span>}
                                  </div>
                                  <div className="cv-word-translations">
                                    <span className="cv-word-uz">{word.translationUz}</span>
                                    {/* English is optional */}
                                    {word.translationEn && (
                                      <>
                                        <span className="cv-word-sep">·</span>
                                        <span className="cv-word-en">{word.translationEn}</span>
                                      </>
                                    )}
                                  </div>
                                  {word.exampleRu && <p className="cv-word-example">{word.exampleRu}</p>}
                                </div>

                                <div className="cv-word-actions">
                                  <button
                                    type="button"
                                    className="ui-icon-btn"
                                    title={t('vocabulary.edit')}
                                    aria-label={t('vocabulary.edit')}
                                    onClick={() => { setEditingId(word.id); setShowAdd(null); }}
                                  >
                                    <Pencil aria-hidden="true" />
                                  </button>
                                  <ActionMenu
                                    ariaLabel={t('vocabulary.moreActions')}
                                    items={[
                                      {
                                        key: 'up',
                                        label: t('vocabulary.moveUp'),
                                        disabled: wIdx === 0 || reordering,
                                        onSelect: () => handleMove(lesson.lessonId, wIdx, -1),
                                      },
                                      {
                                        key: 'down',
                                        label: t('vocabulary.moveDown'),
                                        disabled: wIdx === words.length - 1 || reordering,
                                        onSelect: () => handleMove(lesson.lessonId, wIdx, 1),
                                      },
                                      { key: 'sep', separator: true },
                                      {
                                        key: 'delete',
                                        label: t('vocabulary.delete'),
                                        tone: 'danger',
                                        disabled: deleting === word.id,
                                        onSelect: () => handleDelete(lesson.lessonId, word.id),
                                      },
                                    ]}
                                  />
                                </div>
                              </div>
                            )
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
      {confirmDialog}
    </div>
  );
}
